<template>
	<q-table
		ref="table"
		:data="participants"
		:columns="columns"
		row-key="id"
		:filter="filter"
		@request="requestParticipants"
		:loading="loading"
		:pagination.sync="pagination"
		:no-data-label="$t('no_data_avaliable')"
		:no-results-label="$t('no_results_found')"
		:loading-label="$t('loading_data')"
		:rows-per-page-label="$t('items_per_page')"
		:rows-per-page-options="[5, 10, 50, 100]"
		binary-state-sort
		class="q-mt-lg"
	>
		<template slot="top" slot-scope="props" :props="props">
			<q-search
				v-model="filter"
				icon="fas fa-search"
				:placeholder="$t('search')"
				:debounce="500"
			/>

			<div class="col" />
			<q-btn
				color="secondary"
				class="q-mr-sm"
				icon="fas fa-download"
				:label="$t('participants_report')"
				@click="downloadReport()"
			/>
		</template>

		<q-tr slot="header" slot-scope="props" :props="props">
			<q-th v-for="col in props.cols" :key="col.name" :props="props">
				{{ $t(col.label) }}
			</q-th>
		</q-tr>

		<template slot="body" slot-scope="props">
			<q-tr :props="props">
				<q-td key="full_name" :props="props">
					{{ props.row.full_name }}
				</q-td>

				<q-td key="email" :props="props">
					{{ props.row.email }}
				</q-td>

				<q-td key="profile" :props="props">
					{{ translateProfiles(props.row.profile) }}
				</q-td>

				<q-td key="live" :props="props">
					{{ props.row.live ? $t('yes') : $t('no') }}
				</q-td>

				<q-td key="branches" :props="props">
					<q-btn
						color="primary"
						text-color="white"
						:label="props.row.branches.length"
						no-ripple
					>
						<q-tooltip>
							<p v-if="props.row.branches.length > 0" class="tooltip q-mb-sm">
								{{ $t('branches') }}:
								<span v-for="(branche, index) in props.row.branches" :key="index">
									{{ branche }}
									<span v-if="props.row.branches.length - 1 > index">, </span>
								</span>
							</p>
						</q-tooltip>
					</q-btn>
				</q-td>
			</q-tr>
		</template>
	</q-table>
</template>

<script>
import { mapGetters } from 'vuex'
import { db } from 'plugins/vuefire'

export default {
	props: ['load', 'id', 'webcast_type'],

	data() {
		return {
			pagination: {
				page: 1,
				rowsPerPage: 5,
				descending: false,
				sortBy: null,
			},
			filter: '',
			columns: [
				{
					name: 'full_name',
					required: true,
					label: 'name',
					align: 'left',
					field: 'full_name',
					sortable: true,
				},
				{
					name: 'email',
					required: true,
					label: 'email',
					align: 'left',
					field: 'email',
					sortable: true,
				},
				{
					name: 'profile',
					required: true,
					label: 'profile',
					align: 'left',
					field: 'profile',
					sortable: false,
				},
				{
					name: 'live',
					required: true,
					label: 'watched_live',
					align: 'left',
					field: 'live',
					sortable: false,
				},
				{
					name: 'branches',
					required: true,
					label: 'branches',
					align: 'left',
					field: 'branches',
					sortable: false,
				},
			],
			participants: [],
			loading: false,
		}
	},

	computed: {
		...mapGetters({
			access_token: 'common/access_token',
			id_user_language_selected: 'common/id_user_language_selected',
			num_rows: 'datatable/num_rows',
			profiles: 'registration_lists/profiles',
		}),

		getHeader() {
			let headers = {}
			return (headers = {
				headers: {
					'content-type': 'application/json',
					authorization:
						'Basic ' +
						btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
					authorization: 'Bearer ' + this.access_token,
				},
			})
		},

		getMaxWidth() {
			if (this.$q.platform.is.mobile) return '100vw'
			else return '80vw'
		},

		getMinWidth() {
			if (!this.$q.platform.is.mobile) return '90vw'
		},
	},

	methods: {
		requestParticipants({ pagination }) {
			this.loading = true
			this.pagination = pagination
			this.participants = []

			let order = pagination.descending ? '-' : ''
			let order_by = pagination.sortBy ? order + pagination.sortBy : ''

			let data = {
				page_size: this.pagination.rowsPerPage,
				page: this.pagination.page,
				ordering: order_by,
				search: this.filter,
			}

			this.$axios
				.get(
					`/admin_webcasts/webcasts/${
						this.id
					}/webcast_user_results/load_webcast_participant_users/?${this.$qs.stringify(
						data
					)}`,
					this.getHeader
				)
				.then((response) => {
					this.pagination.rowsNumber = response.data.length

					this.participants = response.data.map((e) => {
						return {
							...e,
							live: this.webcast_type && this.webcast_type === 4 ? true : e.live,
						}
					})
				})
				.catch((e) => {
					console.log(e)
				})
				.finally(() => {
					this.loading = false
				})
		},

		downloadReport() {
			let message = this.$t('generating_import_file_message')
			this.$q.loading.show({
				message,
			})
			this.$axios
				.get(
					`admin_webcasts/webcasts/${this.id}/webcast_user_results/generate_report/`,
					this.getHeader
				)
				.then((response) => {
					this.forceFileDownload(response.data.path_url)
				})
				.catch((e) => {
					this.loading = false
					console.log(e)
				})
				.finally(() => {
					this.$q.loading.hide()
				})
		},

		forceFileDownload(response) {
			const link = document.createElement('a')
			link.href = response
			document.body.appendChild(link)
			link.click()
		},

		translateProfiles(value) {
			let profile = value.toUpperCase()
			let term = this.profiles.find((e) => e.translations[0].name.toUpperCase() === profile)
			if (term) {
				let translated = term.label.toLowerCase()
				return translated.charAt(0).toUpperCase() + translated.slice(1)
			} else return value
		},
	},

	watch: {
		load: function(e) {
			if (this.load && this.id) this.requestParticipants({ pagination: this.pagination })
		},
		webcast_type: function(e) {
			if (this.webcast_type && this.webcast_type === 4) {
				this.columns[3].label = 'watched'
			}
		},
	},

	mounted() {
		this.requestParticipants({ pagination: this.pagination })
	},
}
</script>

<style></style>
